'use client';

import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { formatScheduledTime, type PublicMatch } from '../../libs/scheduleStatus';
import { winnerSide } from '../../libs/gridLive';
import { formatScoreLine } from '@/components/match/formatScoreLine';
import { displayMatchCode } from '@/app/[locale]/event/[eventId]/libs/matchCode';

interface MatchDetailSheetProps {
  match: PublicMatch | null;
  courtName: string;
  delayMin: number;
  onClose: () => void;
  playerHref?: (playerId: string, divisionId?: string | null) => string;
}

export function MatchDetailSheet({
  match,
  courtName,
  delayMin,
  onClose,
  playerHref,
}: MatchDetailSheetProps) {
  const locale = useLocale();
  const t = useTranslations('PublicSchedule');
  const tMatches = useTranslations('Matches');
  const matchCode = displayMatchCode(match?.code);

  const isLive = match?.status === 'live';
  const isDone = match?.status === 'completed';
  const hasScorecard = !!match?.matchId && (isLive || isDone);

  const p1 = match?.players[0] ?? t('tbd');
  const p2 = match?.players[1] ?? t('tbd');
  const p1Roster = match?.rosters?.[0] ?? null;
  const p2Roster = match?.rosters?.[1] ?? null;
  const p1Score = match?.score?.p1 ?? null;
  const p2Score = match?.score?.p2 ?? null;
  const winner = match ? winnerSide(match) : null;
  const retired = match?.outcome === 'retired' || !!match?.retiredPlayerId;
  // A retirement's winner is winner_id; otherwise the higher games total (unchanged).
  const p1Won = retired ? winner === 0 : match?.score ? match.score.p1 > match.score.p2 : false;
  const p2Won = retired ? winner === 1 : match?.score ? match.score.p2 > match.score.p1 : false;
  const livePts = isLive ? (match?.liveGame ?? null) : null;

  return (
    <Dialog open={!!match} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        {match && (
          <>
            {/* Header */}
            <div className="border-border flex items-center justify-between border-b p-5">
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[0.6875rem] font-bold tracking-wider uppercase',
                  isLive && 'bg-danger text-white',
                  isDone && 'bg-success/15 text-success',
                  !isLive && !isDone && 'bg-surface text-muted',
                )}
              >
                {isLive && (
                  <span className="relative inline-flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                  </span>
                )}
                {isLive ? t('statusLive') : isDone ? t('statusCompleted') : t('statusUpcoming')}
              </span>
              <button
                type="button"
                onClick={onClose}
                aria-label={t('close')}
                className="text-muted hover:bg-surface cursor-pointer rounded-md p-1 transition-colors"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Players + score (scoreboard) */}
            <div className="p-5">
              <div className="flex flex-col gap-2">
                <div className="border-border flex items-start justify-between gap-3 border-b pb-2">
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block text-base leading-tight',
                        p1Won ? 'text-foreground font-bold' : 'font-medium',
                        match.score && !p1Won && 'text-muted',
                      )}
                    >
                      {match.playerIds?.[0] && playerHref ? (
                        <Link href={playerHref(match.playerIds[0], match.divisionId)}>{p1}</Link>
                      ) : (
                        p1
                      )}
                    </span>
                    {p1Roster && (
                      <span className="text-muted block text-xs whitespace-pre-line">
                        {p1Roster}
                      </span>
                    )}
                  </span>
                  {p1Score !== null && (
                    <span className="flex shrink-0 items-baseline gap-2 font-mono tabular-nums">
                      {livePts && <span className="text-muted text-sm">{livePts.p1}</span>}
                      <span
                        className={cn(
                          'text-2xl',
                          p1Won ? 'text-foreground font-bold' : 'text-muted font-medium',
                        )}
                      >
                        {p1Score}
                      </span>
                    </span>
                  )}
                </div>
                <div className="flex items-start justify-between gap-3">
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block text-base leading-tight',
                        p2Won ? 'text-foreground font-bold' : 'font-medium',
                        match.score && !p2Won && 'text-muted',
                      )}
                    >
                      {match.playerIds?.[1] && playerHref ? (
                        <Link href={playerHref(match.playerIds[1], match.divisionId)}>{p2}</Link>
                      ) : (
                        p2
                      )}
                    </span>
                    {p2Roster && (
                      <span className="text-muted block text-xs whitespace-pre-line">
                        {p2Roster}
                      </span>
                    )}
                  </span>
                  {p2Score !== null && (
                    <span className="flex shrink-0 items-baseline gap-2 font-mono tabular-nums">
                      {livePts && <span className="text-muted text-sm">{livePts.p2}</span>}
                      <span
                        className={cn(
                          'text-2xl',
                          p2Won ? 'text-foreground font-bold' : 'text-muted font-medium',
                        )}
                      >
                        {p2Score}
                      </span>
                    </span>
                  )}
                </div>
                {(match.games?.length ?? 0) > 0 && (
                  <p className="text-muted mt-1 font-mono text-xs tabular-nums">
                    {formatScoreLine(
                      match.games!.map((g) => ({ player1: g.p1, player2: g.p2 })),
                      { retired, marker: tMatches('retiredShort') },
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* Meta */}
            <div className="bg-surface px-5 py-3 text-sm">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
                {matchCode && (
                  <>
                    <dt className="text-muted">{t('matchCode')}</dt>
                    <dd className="text-foreground font-mono font-semibold tracking-wider">{matchCode}</dd>
                  </>
                )}
                {match.division && (
                  <>
                    <dt className="text-muted">{t('division')}</dt>
                    <dd className="text-foreground font-medium">{match.division}</dd>
                  </>
                )}
                {match.stage && (
                  <>
                    <dt className="text-muted">{t('round')}</dt>
                    <dd className="text-foreground font-medium">{match.stage}</dd>
                  </>
                )}
                <dt className="text-muted">{t('courtLabel')}</dt>
                <dd className="text-foreground font-medium">{courtName}</dd>
                <dt className="text-muted">{t('scheduled')}</dt>
                <dd className="text-foreground font-medium">
                  <LocalDateTime
                    iso={match.scheduledAt}
                    locale={locale}
                    format={formatScheduledTime}
                  />
                  {delayMin > 0 && !isDone && (
                    <span className="text-warning ml-2">
                      {t('delayMinutes', { minutes: delayMin })}
                    </span>
                  )}
                </dd>
              </dl>
            </div>

            {/* Actions */}
            <div className="border-border flex gap-2 border-t p-4">
              {hasScorecard && match.matchId && (
                <Link
                  href={`/match/scorecard/${match.matchId}`}
                  target="_blank"
                  rel="noopener"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 flex-1 rounded-md px-4 py-2 text-center text-sm font-semibold transition-colors"
                >
                  {t('viewScorecard')}
                </Link>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className={hasScorecard ? '' : 'flex-1'}
              >
                {t('close')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
